/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, CircularProgress } from '@mic3/platform-ui';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { withTheme } from 'styled-components';

import Container from 'app/components/atoms/Container/Container';
import Loader from 'app/components/atoms/Loader/Loader';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import ProcessDiagramCard from 'app/components/ABox/Cards/ProcessDiagramCard';
import { loadProcessSidebarDetails, updateProcess, updateProcessSidebarDetailsFromSubscription } from 'store/actions/abox/processActions';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';

import { getNum, isEmpty } from 'app/utils/utils';
import { PRIORITY_OPTIONS, getPriorityColor } from 'app/config/aboxConfig';
import { get } from 'app/utils/lo/lo';
import { loadUserReferences } from 'store/actions/admin/usersActions';
import { processSubscriptionUpdate } from 'app/utils/app/appUtils';
import EntityChangeSubscription from 'app/containers/App/Subscriptions/EntityChangeSubscription';
import { PROCESS_CHANGE_SUBSCRIPTION } from 'graphql/app/appSubscriptions';

// Fields to track for real-time updates
const PROCESS_TRACKED_FIELDS = [
    'primary.assignee',
    'primary.priority',
];

class ProcessAboutTab extends PureComponent {
    static propTypes = {
        details: PropTypes.object.isRequired,
        reloadList: PropTypes.func,
    };

    formRef = React.createRef();
    // Flag to skip form reset when Redux updates from subscription
    isSubscriptionUpdate = false;

    constructor(props) {
        super(props);
        this.state = {
            data: props.details,
        };
    }

    componentDidUpdate(prevProps) {
        const { userReferencesLoading, processInitiator, details } = this.props;
        if (!userReferencesLoading && !processInitiator) {
            this.props.loadUserReferences();
        }
        if (details !== prevProps.details) {
            // Skip form reset if this update came from subscription
            if (this.isSubscriptionUpdate) {
                this.isSubscriptionUpdate = false;
                return;
            }
            this.setState({ data: details });
        }
    }

    @bind
    async onEntityChange(subscriptionData) {
        const { details, internal, updateProcessSidebarDetailsFromSubscription } = this.props;

        // Set flag BEFORE Redux dispatch to prevent componentDidUpdate from overwriting form state
        this.isSubscriptionUpdate = true;

        const mergedData = await processSubscriptionUpdate({
            formRef: this.formRef,
            reduxState: details,
            subscriptionData,
            trackedFields: PROCESS_TRACKED_FIELDS,
            updateRedux: (data) => updateProcessSidebarDetailsFromSubscription(data, internal),
        });

        if (!mergedData) {
            this.isSubscriptionUpdate = false;
            return;
        }

        // Update local form state with merged data
        this.setState({ data: mergedData });
    }

    @bind
    @memoize()
    formDefinition(details) {
        const { canEdit, theme } = this.props;
        const { primary } = details;
        const { closureReason, closedDate, priority } = primary;
        const disabled = !canEdit || !!closedDate;
        const priorityColor = !!closedDate ? 'disabled' : getPriorityColor(priority);
        return [
            {
                type: 'panel',
                properties: {
                    header: 'About',
                    expanded: true,
                    style: { borderTop: `2px solid ${theme.priorityGradients[priorityColor][0]}` },
                },
                children: [
                    closureReason && {
                        type: 'text',
                        properties: {
                            label: 'Reason for cancelling process',
                            name: 'primary.closureReason',
                            disabled: true,
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Description',
                            name: 'processDefinitionVersion.description',
                            disabled: true,
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Started By',
                            name: 'initiatorName',
                            disabled: true,
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Application Name',
                            name: 'name',
                            disabled: true,
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Definition Name',
                            name: 'processDefinitionVersion.name',
                            disabled: true,
                        },
                    },
                    {
                        type: 'userTypeahead',
                        properties: { name: 'primary.assignee', label: 'Assignee', valueField: 'id', disabled },
                    },
                    {
                        type: 'typeahead',
                        properties: {
                            label: 'Priority',
                            name: 'primary.priority',
                            valueField: 'value',
                            options: PRIORITY_OPTIONS,
                            clearable: false,
                            defaultValue: priority === 50 || !priority ? 3 : Number(priority),
                            disabled,
                        },
                    },
                ].filter(Boolean),
            },
            {
                type: 'panel',
                properties: { header: 'Properties', expanded: true },
                children: [
                    {
                        type: 'number',
                        properties: {
                            name: 'primary.sequenceNumber',
                            label: 'Sequence number',
                            disabled: true,
                        },
                    },
                    {
                        type: 'dateTime',
                        properties: {
                            label: 'Created Date',
                            name: 'createdDate',
                            disabled: true,
                        },
                    },
                    {
                        type: 'dateTime',
                        properties: {
                            label: 'Last Updated',
                            name: 'modifiedDate',
                            disabled: true,
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Status',
                            name: 'status13',
                            defaultValue: closedDate ? 'Closed' : 'Open',
                            disabled: true,
                        },
                    },
                ],
            },
            {
                type: 'panel',
                properties: { header: 'Process Diagram', expanded: true },
                children: [
                    {
                        type: 'custom',
                        properties: {
                            name: 'processDefinitionVersion.primary.definition',
                            Component: props => <ProcessDiagramCard {...(props || {})} />,
                        },
                    },
                ],
            },
        ];
    }

    @bind
    loadDetails() {
        const {
            details: { id },
            loadProcessSidebarDetails,
        } = this.props;
        return loadProcessSidebarDetails(id);
    }

    @bind
    async onFormSubmit(event) {
        event.preventDefault();
        const { reloadList, updateProcess, internal } = this.props;
        const { errors, data } = await this.formRef.current.isValidForm();
        if (!errors) {
            const { id, primary } = data;
            const { assignee, priority } = primary || {};
            await updateProcess(
                {
                    id,
                    primary: { assignee, priority },
                },
                internal
            );
            reloadList && (await reloadList());
        }
    }

    @bind
    @memoize()
    normalizeForSave(process, processInitiator) {
        return {
            ...process,
            primary: {
                ...get(process, 'primary', {}),
                priority: getNum(process, 'primary.priority', 3),
            },
            initiatorName: get(processInitiator, 'name', ''),
        };
    }

    @bind
    @memoize()
    renderRightActions(sidebarActions, isLoading, type) {
        const { canEdit } = this.props;
        if (type === 'closedprocess' || !canEdit) {
            return sidebarActions;
        }
        return (
            <>
                {isLoading ? (
                    <CircularProgress key={113} size={24} color="primary" />
                ) : (
                    <Button key={113} onClick={this.onFormSubmit} color="primary" form="form" type="submit">
                        Save
                    </Button>
                )}
                {sidebarActions}
            </>
        );
    }

    /**
     * @override
     */
    render() {
        const { details, processInitiator, isLoading, sidebarActions, breadcrumbLine, isSidebar } = this.props;
        const { data } = this.state;
        const process = this.normalizeForSave(data, processInitiator);
        if (isEmpty(details)) {
            return <Loader absolute />;
        }
        return (
            <>
                <EntityChangeSubscription
                    id={details.id}
                    type={'system_process'}
                    subscriptionQuery={PROCESS_CHANGE_SUBSCRIPTION}
                    onEntityClosed={this.loadDetails}
                    onEntityChange={this.onEntityChange}
                />
                <HeaderBar right={this.renderRightActions(sidebarActions, isLoading, get(process, 'type'))} left={breadcrumbLine} id={details?.id} />
                <ContentArea withHeader isSidebar={isSidebar}>
                    <Container noPadding>
                        <FormGenerator ref={this.formRef} components={this.formDefinition(process)} data={process} />
                    </Container>
                </ContentArea>
            </>
        );
    }
}

export default connect(
    (state, props) => ({
        processInitiator: (state.admin.users.references.data || []).find(usr => usr.id === get(props.details, 'primary.variables.INITIATOR')),
        userReferencesLoading: state.admin.users.references.isLoading,
    }),
    {
        loadProcessSidebarDetails,
        updateProcess,
        updateProcessSidebarDetailsFromSubscription,
        loadUserReferences,
    }
)(withTheme(ProcessAboutTab));
